/**
 * PlatformIntegrationsPage — Tab "APIs" de la consola de plataforma (FF-PLATFORM-01).
 *
 * Muestra las integraciones agrupadas por categoría con sus estados y acciones:
 *   - Badge de estado (Configurada / No configurada / Error)
 *   - Secreto enmascarado con últimos 4 chars siempre visibles
 *   - Botón "Revelar" (GET /secret, con confirmación, auto-oculta en 30 s)
 *   - Botón "Editar" (formulario PUT con secret + metadata)
 *   - Botón "Probar conexión" (POST /test)
 *
 * Sigue el design system Cumple V2 y el patrón de PlatformBilling.tsx.
 * ADR-039: contrato de API con el backend (snake_case en la red).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppShell } from '../components/AppShell.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import {
  platformIntegrationsApi,
  type Integration,
  type IntegrationCategory,
  type IntegrationStatus,
} from '../api/platform-integrations.js';

// ─────────────────────────────────────────────────────────────────────────────
// Constantes de presentación
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  ai: 'Inteligencia artificial',
  email: 'Correo electrónico',
  storage: 'Almacenamiento',
  payments: 'Pasarelas de pago',
};

/** Orden de renderizado de categorías */
const CATEGORY_ORDER: IntegrationCategory[] = ['ai', 'email', 'storage', 'payments'];

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export function PlatformIntegrationsPage(): JSX.Element {
  const { setBackTo } = useAppShell();

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBackTo(null);
    return () => setBackTo(null);
  }, [setBackTo]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    platformIntegrationsApi
      .list()
      .then(({ integrations: list }) => {
        setIntegrations(list);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Error al cargar las integraciones.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Actualiza una integración en el estado local sin recargar toda la lista.
  function patchIntegration(updated: Integration): void {
    setIntegrations((prev) => prev.map((i) => (i.provider === updated.provider ? updated : i)));
  }

  if (loading)
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <LoadingSkeleton rows={6} />
      </div>
    );

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={load}
            className="text-sm font-medium text-red-700 underline hover:no-underline"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!integrations.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState
          title="Sin integraciones configuradas"
          description="No hay integraciones registradas en la plataforma."
        />
      </div>
    );
  }

  // Agrupa por categoría respetando el orden canónico.
  const grouped = CATEGORY_ORDER.reduce<Record<string, Integration[]>>((acc, cat) => {
    const items = integrations.filter((i) => i.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <h2 className="text-lg font-medium text-gray-900">Integraciones y secretos de plataforma</h2>

      {(Object.entries(grouped) as [IntegrationCategory, Integration[]][]).map(
        ([category, items]) => (
          <IntegrationGroup
            key={category}
            category={category}
            integrations={items}
            onUpdate={patchIntegration}
          />
        ),
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IntegrationGroup: sección por categoría
// ─────────────────────────────────────────────────────────────────────────────

function IntegrationGroup({
  category,
  integrations,
  onUpdate,
}: {
  category: IntegrationCategory;
  integrations: Integration[];
  onUpdate: (updated: Integration) => void;
}): JSX.Element {
  return (
    <section>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        {CATEGORY_LABELS[category]}
      </h3>
      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white overflow-hidden">
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.provider}
            integration={integration}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IntegrationCard: fila de una integración
// ─────────────────────────────────────────────────────────────────────────────

function IntegrationCard({
  integration,
  onUpdate,
}: {
  integration: Integration;
  onUpdate: (updated: Integration) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [testState, setTestState] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  async function handleTest(): Promise<void> {
    setTestState('testing');
    setTestMessage(null);
    try {
      const res = await platformIntegrationsApi.testConnection(integration.provider);
      onUpdate({ ...integration, status: res.status, last_tested_at: res.last_tested_at });
      setTestState('ok');
      setTestMessage(null);
      // Reset badge tras 3 s
      setTimeout(() => setTestState('idle'), 3000);
    } catch (e) {
      setTestState('error');
      setTestMessage(e instanceof Error ? e.message : 'Error de conexión');
    }
  }

  const providerLabel = formatProviderLabel(integration.provider);

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-1">
          {/* Nombre + badge estado */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 text-sm">{providerLabel}</span>
            <IntegrationStatusBadge status={integration.status} />
          </div>

          {/* Secreto enmascarado */}
          {(integration.status === 'configured' || integration.last4) && (
            <MaskedSecret
              label={`clave API de ${providerLabel}`}
              last4={integration.last4}
              provider={integration.provider}
            />
          )}

          {/* Metadata visible (modelo, bucket, etc.) */}
          {Object.entries(integration.metadata).map(([key, val]) =>
            val ? (
              <p key={key} className="text-xs text-gray-500">
                {formatMetaKey(key)}: <span className="text-gray-700">{val}</span>
              </p>
            ) : null,
          )}

          {/* Timestamp último test */}
          {integration.last_tested_at && (
            <p className="text-xs text-gray-400">
              Último test: {new Date(integration.last_tested_at).toLocaleString('es-DO')}
            </p>
          )}
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {integration.status === 'configured' && (
            <ConnectionTestButton
              state={testState}
              message={testMessage}
              onTest={() => void handleTest()}
              integrationName={providerLabel}
            />
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-sm font-medium text-cumple-primary-700 hover:text-cumple-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-2 rounded"
          >
            {integration.status === 'not_configured' ? '+ Configurar' : 'Editar'}
          </button>
        </div>
      </div>

      {/* Mensaje de error del test (inline, no modal) */}
      {testState === 'error' && testMessage && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          No se pudo conectar: {testMessage}
        </p>
      )}

      {/* Formulario de edición inline */}
      {editing && (
        <IntegrationEditForm
          integration={integration}
          onSave={(updated) => {
            onUpdate(updated);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MaskedSecret: muestra los últimos 4 chars y ofrece revelar el secreto
// ─────────────────────────────────────────────────────────────────────────────

const REVEAL_TIMEOUT_MS = 30_000;
const COUNTDOWN_ANNOUNCE_MS = 10_000;

function MaskedSecret({
  label,
  last4,
  provider,
}: {
  label: string;
  last4: string | null;
  provider: string;
}): JSX.Element {
  const [state, setState] = useState<'masked' | 'loading' | 'revealed'>('masked');
  const [secret, setSecret] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [announceCountdown, setAnnounceCountdown] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function clearTimers(): void {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }

  function hide(): void {
    clearTimers();
    setState('masked');
    setSecret(null);
    setCountdown(null);
    setAnnounceCountdown(false);
  }

  async function handleReveal(): Promise<void> {
    if (state === 'loading') return;
    setState('loading');
    try {
      const { secret: value } = await platformIntegrationsApi.revealSecret(provider);
      setSecret(value);
      setState('revealed');

      // Countdown visual: empieza en 30, anuncia aria-live a los 10 s
      setCountdown(REVEAL_TIMEOUT_MS / 1000);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearTimers();
            hide();
            return null;
          }
          const next = prev - 1;
          if (next === COUNTDOWN_ANNOUNCE_MS / 1000) {
            setAnnounceCountdown(true);
          }
          return next;
        });
      }, 1000);

      timerRef.current = setTimeout(hide, REVEAL_TIMEOUT_MS);
    } catch {
      setState('masked');
    }
  }

  // Limpiar timers al desmontar
  useEffect(() => () => clearTimers(), []);

  async function handleCopy(): Promise<void> {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
    } catch {
      // Si el clipboard API falla (contexto no-seguro), ignorar silenciosamente
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap mt-1">
      {/* Secreto: enmascarado o revelado */}
      <span
        className="font-mono text-xs text-gray-700 select-none"
        aria-hidden={state === 'revealed'}
      >
        {state === 'revealed' && secret ? (
          <span className="text-gray-900 select-all">{secret}</span>
        ) : (
          <>
            <span className="text-gray-400">{'••••••••••••••••••••••••••••'}</span>
            {last4 && <span className="text-gray-700">{last4}</span>}
          </>
        )}
      </span>

      {/* Acciones */}
      {state === 'masked' && (
        <button
          type="button"
          aria-label={`Revelar ${label}`}
          onClick={() => void handleReveal()}
          className="text-xs font-medium text-cumple-primary-700 hover:text-cumple-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-1 rounded"
        >
          Revelar
        </button>
      )}
      {state === 'loading' && <span className="text-xs text-gray-400">Cargando...</span>}
      {state === 'revealed' && (
        <>
          <button
            type="button"
            aria-label={`Ocultar ${label}`}
            onClick={hide}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-1 rounded"
          >
            Ocultar
          </button>
          <button
            type="button"
            aria-label={`Copiar ${label} al portapapeles`}
            onClick={() => void handleCopy()}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-1 rounded"
          >
            Copiar
          </button>
          {countdown !== null && (
            <span className="text-xs text-gray-400">Se oculta en {countdown} s</span>
          )}
        </>
      )}

      {/* Anuncio aria-live a los 10 s finales */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announceCountdown && countdown !== null
          ? `La clave se ocultará en ${countdown} segundos`
          : ''}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ConnectionTestButton
// ─────────────────────────────────────────────────────────────────────────────

function ConnectionTestButton({
  state,
  message,
  onTest,
  integrationName,
}: {
  state: 'idle' | 'testing' | 'ok' | 'error';
  message: string | null;
  onTest: () => void;
  integrationName: string;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={state === 'testing'}
      onClick={onTest}
      aria-label={`Probar conexión de ${integrationName}`}
      className={[
        'text-xs font-medium px-2 py-1 rounded border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
        state === 'ok'
          ? 'border-green-300 bg-green-50 text-green-700'
          : state === 'error'
            ? 'border-red-300 bg-red-50 text-red-700'
            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50',
      ].join(' ')}
    >
      {state === 'testing'
        ? 'Probando...'
        : state === 'ok'
          ? 'Conexión exitosa'
          : state === 'error' && message
            ? `Error`
            : 'Probar conexión'}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IntegrationEditForm: formulario inline para actualizar secreto + metadata
// ─────────────────────────────────────────────────────────────────────────────

function IntegrationEditForm({
  integration,
  onSave,
  onCancel,
}: {
  integration: Integration;
  onSave: (updated: Integration) => void;
  onCancel: () => void;
}): JSX.Element {
  const [secret, setSecret] = useState('');
  const [metadata, setMetadata] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(integration.metadata).map(([k, v]) => [k, v ?? ''])),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    const hasSecret = secret.trim().length > 0;
    // Para configurar por primera vez el secreto es obligatorio; sin él no hay
    // nada que cifrar y el submit sería un no-op silencioso.
    if (!hasSecret && integration.status === 'not_configured') {
      setError('Ingresa el secreto para configurar esta integración.');
      return;
    }

    const body: { secret?: string; metadata?: Record<string, string | null> } = {};
    if (hasSecret) body.secret = secret.trim();
    const cleanMeta: Record<string, string | null> = Object.fromEntries(
      Object.entries(metadata).map(([k, v]) => [k, v.trim() || null]),
    );
    if (Object.keys(cleanMeta).length > 0) body.metadata = cleanMeta;

    if (!body.secret && !body.metadata) {
      setError('No hay cambios para guardar.');
      return;
    }

    setSaving(true);
    try {
      const updated = await platformIntegrationsApi.update(integration.provider, body);
      onSave(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4"
    >
      <h4 className="text-sm font-medium text-gray-900">
        Editar {formatProviderLabel(integration.provider)}
      </h4>

      {/* Secreto (siempre opcional — vacío = no cambiar) */}
      <div>
        <label
          htmlFor={`secret-${integration.provider}`}
          className="block text-xs font-medium text-gray-700 mb-1"
        >
          Nuevo secreto <span className="text-gray-400">(dejar vacío para no cambiar)</span>
        </label>
        <input
          id={`secret-${integration.provider}`}
          type="password"
          autoComplete="new-password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          className="w-full text-sm rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cumple-primary-600"
          placeholder="sk-ant-..."
        />
      </div>

      {/* Metadata editable */}
      {Object.entries(metadata).map(([key, val]) => (
        <div key={key}>
          <label
            htmlFor={`meta-${integration.provider}-${key}`}
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            {formatMetaKey(key)}
          </label>
          <input
            id={`meta-${integration.provider}-${key}`}
            type="text"
            value={val}
            onChange={(e) => setMetadata((prev) => ({ ...prev, [key]: e.target.value }))}
            className="w-full text-sm rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cumple-primary-600"
          />
        </div>
      ))}

      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="text-sm text-gray-600 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-2 rounded disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="text-sm font-medium bg-cumple-primary-700 text-white rounded px-4 py-2 hover:bg-cumple-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-primary-600 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IntegrationStatusBadge
// ─────────────────────────────────────────────────────────────────────────────

function IntegrationStatusBadge({ status }: { status: IntegrationStatus }): JSX.Element {
  const styles: Record<IntegrationStatus, string> = {
    configured: 'bg-green-100 text-green-800',
    not_configured: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
  };
  const labels: Record<IntegrationStatus, string> = {
    configured: 'Configurada',
    not_configured: 'No configurada',
    error: 'Error',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilidades de presentación
// ─────────────────────────────────────────────────────────────────────────────

function formatProviderLabel(provider: string): string {
  const labels: Record<string, string> = {
    anthropic: 'Anthropic Claude',
    postmark: 'Postmark',
    smtp: 'SMTP genérico',
    ovh_s3: 'OVH Object Storage / S3',
    stripe: 'Stripe',
    azul: 'Azul',
    cardnet: 'CardNet',
  };
  return labels[provider] ?? provider;
}

function formatMetaKey(key: string): string {
  const labels: Record<string, string> = {
    model: 'Modelo activo',
    bucket: 'Bucket',
    region: 'Región',
    from_email: 'Email remitente',
    host: 'Host',
    port: 'Puerto',
    user: 'Usuario',
    merchant_id: 'Merchant ID',
    terminal_id: 'Terminal ID',
    publishable_key: 'Publishable key',
    gateway: 'Gateway',
  };
  return labels[key] ?? key;
}
